import {
    effectiveFloor,
    effectiveOneTime,
    oneTimeInMonth,
    parsePayPeriodParams,
    policyDatesInMonth,
    policyOwnedLineKeys,
    isBalancedPrefAccrualReclass,
    scheduleIsValid,
} from './engine/cashPolicy';
import { loadCashFlowSchema } from './engine/cashFlowSchema';
import { daysInMonth, monthKeyFromT, tFromMonthKey } from './engine/calendar';
import { wouldCreateCycle } from './engine/dag';
import { distributionPartnerKeys } from './engine/lineKeys';
import { allowedDrivers, loadSchema } from './engine/schema';
import {
    isGroupIgnored,
    isGroupPartiallyIgnored,
    listEligibleIgnoreGroups,
} from './engine/ignoreGroups';
import type {
    CaseInputs,
    History,
    Model,
    SchemaEntry,
    ValidationIssue,
    ValidationOptions,
    ValidationResult,
    ValidationSection,
    ValidationThresholds,
    View,
} from './engine/types';
import { FIXED_DRIVERS, LOCATION_VIEWS } from './engine/types';
import { materialise } from './caseJson/recipe';
import { resolveSeasonalityFactors } from './caseJson/recipeHistory';
import { projectionMonthKeys } from './caseJson/utils';
import { driverInputUnit } from './driverUtils';
import { evalFormula, formulaDependencies } from './engine/formulas';
import { DEFAULT_THRESHOLDS, VALIDATION_CODES } from './validationCodes';

const STORE_STATUS_KEY = 'is.store_status';
const AVG_ORDERS_KEY = 'is.avg_orders_per_day';
const TOTAL_ORDERS_KEY = 'is.total_orders';
const TICKET_KEY = 'is.avg_ticket';
const GPS_KEY = 'is.gross_product_sales';
const CASH_KEY = 'bs.cash_bank_accounts_and_petty_cash';
const TOTAL_ASSETS_KEY = 'bs.total_assets';
const TOTAL_LE_KEY = 'bs.total_liabilities_and_equity';
const DAYS_OPEN_KEY = 'is.days_open_for_business';
const DAYS_CLOSED_KEY = 'is.days_closed';
const SQFT_KEY = 'is.square_footage';
const BOH_KEY = 'is.boh';
const FOH_KEY = 'is.foh';
const PAYROLL_WAGES_KEY = 'is.payroll_wages';

const LABOR_COMPONENT_KEYS = [
    'is.hourly_fte_boh',
    'is.hourly_fte_foh',
    'is.avg_hourly_rate_boh',
    'is.avg_hourly_rate_foh',
    'is.salaried_headcount',
    'is.avg_salary_annual',
] as const;

function isLaborBuildupActive(inputs: CaseInputs, view: View): boolean {
    return (
        view !== 'consolidated' &&
        inputs.drivers[view]?.[PAYROLL_WAGES_KEY]?.active === 'labor_buildup'
    );
}

function shouldSkipLaborDriverWarning(
    lineKey: string,
    view: View,
    inputs: CaseInputs,
): boolean {
    if ((LABOR_COMPONENT_KEYS as readonly string[]).includes(lineKey) && !isLaborBuildupActive(inputs, view)) {
        return true;
    }
    if (lineKey === BOH_KEY || lineKey === FOH_KEY) {
        return true;
    }
    return false;
}

const TOP_SECTIONS = new Set<string>([
    'Operational Data',
    'Income Statement',
    'Balance Sheet',
]);

function getNumeric(model: Model, view: View, lineKey: string, t: number): number | null {
    const value = model.values[view]?.[lineKey]?.[t];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function buildSectionMap(schema: SchemaEntry[]): Map<string, ValidationSection> {
    const map = new Map<string, ValidationSection>();
    let current: ValidationSection = 'Operational Data';
    for (const entry of schema) {
        if (entry.kind === 'header' && TOP_SECTIONS.has(entry.label.trim())) {
            current = entry.label.trim() as ValidationSection;
        }
        if (entry.key) {
            map.set(entry.key, current);
        }
    }
    return map;
}

function issue(
    schemaByKey: Record<string, SchemaEntry>,
    sectionMap: Map<string, ValidationSection>,
    partial: Omit<ValidationIssue, 'label' | 'section'>,
): ValidationIssue {
    const entry = schemaByKey[partial.line_key];
    const label = entry?.label ?? partial.line_key;
    const section = sectionMap.get(partial.line_key) ?? 'Income Statement';
    return { ...partial, label, section };
}

function isFixedDriverLine(entry: SchemaEntry): boolean {
    if (entry.computed) {
        return true;
    }
    return entry.drivers.length === 1 && FIXED_DRIVERS.includes(entry.drivers[0] as (typeof FIXED_DRIVERS)[number]);
}

function validViewsForLine(
    entry: SchemaEntry,
    consolidatedOnly: string[],
): View[] {
    if (entry.tab === 'BS' || entry.bs_only) {
        return ['consolidated'];
    }
    if (consolidatedOnly.includes(entry.key)) {
        return ['consolidated'];
    }
    return [...LOCATION_VIEWS];
}

function historyValueAtT(
    history: History,
    view: View,
    lineKey: string,
    t: number,
): number | string | null {
    const idx = history.nHistory - 1 + t;
    return history.values[view]?.[lineKey]?.[idx] ?? null;
}

function hasNonzeroLast12History(
    history: History,
    view: View,
    lineKey: string,
    entry: SchemaEntry,
    consolidatedOnly: string[],
): boolean {
    if (consolidatedOnly.includes(lineKey) && entry.tab === 'IS') {
        let sum = 0;
        let has = false;
        for (let t = -11; t <= 0; t += 1) {
            for (const loc of LOCATION_VIEWS) {
                const value = historyValueAtT(history, loc, lineKey, t);
                if (typeof value === 'number' && value !== 0) {
                    sum += value;
                    has = true;
                }
            }
        }
        void sum;
        return has;
    }

    for (let t = -11; t <= 0; t += 1) {
        const value = historyValueAtT(history, view, lineKey, t);
        if (typeof value === 'number' && value !== 0) {
            return true;
        }
    }
    return false;
}

function isStoreOpen(model: Model, history: History, view: View, t: number): boolean {
    const fromHistory = historyValueAtT(history, view, STORE_STATUS_KEY, t);
    const fromModel = model.values[view]?.[STORE_STATUS_KEY]?.[t];
    const status = t <= 0 ? fromHistory : fromModel;
    return String(status ?? '').toLowerCase() === 'open';
}

export function validateCase(
    model: Model,
    inputs: CaseInputs,
    history: History,
    thresholds: ValidationThresholds = DEFAULT_THRESHOLDS,
    options: ValidationOptions = {},
): ValidationResult {
    const schema = loadSchema();
    const schemaByKey = Object.fromEntries(schema.map((entry) => [entry.key, entry]));
    const sectionMap = buildSectionMap(schema);
    const errors: ValidationIssue[] = [];
    const warnings: ValidationIssue[] = [];
    const lastClosed = history.lastClosedMonth;
    const scheduleContext = {
        last_dist_date: options.lastDistDate ?? null,
        last_dist_num: options.lastDistNum ?? null,
        last_closed_month: options.lastClosedMonth ?? lastClosed,
        waterfall_mode: options.waterfallMode ?? null,
        logged_after_close: [],
        tier4_shares: {},
    };
    const isEmptyCase =
        inputs.consolidated_only.length === 0 &&
        Object.keys(inputs.drivers).length === 0 &&
        inputs.one_time_items.length === 0;
    const policyOwnedKeys =
        inputs.cash_policy?.enabled === true ? policyOwnedLineKeys(schema) : new Set<string>();

    for (const [viewName, lines] of Object.entries(inputs.drivers)) {
        const view = viewName as View;
        for (const [lineKey, state] of Object.entries(lines)) {
            if (!state.active) {
                continue;
            }
            const entry = schemaByKey[lineKey];
            if (
                state.active === 'carry_forward' ||
                state.active === 'ignore' ||
                state.active === 'labor_buildup'
            ) {
                continue;
            }
            const values = state.by_type[state.active]?.values ?? [];
            if (values.length !== 36) {
                errors.push(
                    issue(schemaByKey, sectionMap, {
                        code: VALIDATION_CODES.driven_null,
                        line_key: lineKey,
                        view,
                        t: null,
                        message: `${entry?.label ?? lineKey} driver cache must have length 36`,
                    }),
                );
            }
            for (let i = 0; i < 36; i += 1) {
                const value = values[i];
                if (value == null || !Number.isFinite(value)) {
                    errors.push(
                        issue(schemaByKey, sectionMap, {
                            code: VALIDATION_CODES.driven_null,
                            line_key: lineKey,
                            view,
                            t: i + 1,
                            message: `${entry?.label ?? lineKey} driver value is null/NaN at t=${i + 1}`,
                        }),
                    );
                }
            }
            if (state.active === 'pct_of_line' || state.active === 'dollar_per_line_unit') {
                const ref = state.by_type[state.active]?.params?.ref;
                if (ref && wouldCreateCycle(schema, inputs, view, lineKey, ref)) {
                    errors.push(
                        issue(schemaByKey, sectionMap, {
                            code: VALIDATION_CODES.pct_of_line_cycle,
                            line_key: lineKey,
                            view,
                            t: null,
                            message: `${entry?.label ?? lineKey} ${state.active} reference ${schemaByKey[ref]?.label ?? ref} would create a cycle`,
                        }),
                    );
                }
            }
            const cache = state.by_type[state.active];
            const recipe = cache?.recipe;
            if (recipe) {
                const projectionMonths = projectionMonthKeys(lastClosed);
                const seasonalityResult = resolveSeasonalityFactors(recipe, {
                    view: view as View,
                    lineKey,
                    driverType: state.active,
                    params: cache?.params ?? {},
                    history,
                    schema,
                    lastClosedMonth: lastClosed,
                    pathPrefix: `drivers.${view}.${lineKey}.recipe`,
                });
                const expected = materialise(recipe, {
                    lastClosedMonth: lastClosed,
                    driverType: state.active,
                    unit: driverInputUnit(state.active as Parameters<typeof driverInputUnit>[0]),
                    seasonalityFactors: seasonalityResult.factors,
                    projectionMonths,
                });
                const mismatched = expected.some((v, i) => v !== values[i]);
                if (mismatched && !options.skipRecipeMismatch) {
                    errors.push(
                        issue(schemaByKey, sectionMap, {
                            code: VALIDATION_CODES.recipe_mismatch,
                            line_key: lineKey,
                            view,
                            t: null,
                            message: `${entry?.label ?? lineKey} materialised cache does not match its recipe.`,
                        }),
                    );
                }
            }
        }
    }

    for (const group of listEligibleIgnoreGroups(schema)) {
        for (const view of LOCATION_VIEWS) {
            if (isGroupPartiallyIgnored(inputs, view, group)) {
                const parentEntry = schemaByKey[group.parent];
                errors.push(
                    issue(schemaByKey, sectionMap, {
                        code: VALIDATION_CODES.incomplete_ignore_group,
                        line_key: group.parent,
                        view,
                        t: null,
                        message: `${parentEntry?.label ?? group.parent} (${view}) has an incomplete Ignore group.`,
                    }),
                );
            }
        }
    }

    const missing: ValidationIssue[] = [];
    for (const entry of schema) {
        if (
            entry.kind !== 'input' ||
            entry.key === 'is.gross_product_sales' ||
            entry.tab === 'BS' ||
            entry.bs_only ||
            isFixedDriverLine(entry)
        ) {
            continue;
        }
        for (const view of validViewsForLine(entry, inputs.consolidated_only)) {
            if (shouldSkipLaborDriverWarning(entry.key, view, inputs)) {
                continue;
            }
            const memberGroup = listEligibleIgnoreGroups(schema).find((group) =>
                group.members.includes(entry.key),
            );
            if (
                memberGroup &&
                isGroupIgnored(inputs, view, memberGroup)
            ) {
                continue;
            }
            if (allowedDrivers(schema, entry.key, view, inputs.consolidated_only, inputs).length === 0) {
                continue;
            }
            if (!hasNonzeroLast12History(history, view, entry.key, entry, inputs.consolidated_only)) {
                continue;
            }
            if (view === 'consolidated' && policyOwnedKeys.has(entry.key)) {
                continue;
            }
            const active = inputs.drivers[view]?.[entry.key]?.active;
            if (!active) {
                missing.push(
                    issue(schemaByKey, sectionMap, {
                        code: VALIDATION_CODES.missing_driver,
                        line_key: entry.key,
                        view,
                        t: null,
                        message: `${entry.label} (${view}) has history but no active driver.`,
                    }),
                );
            }
        }
    }
    missing.sort((a, b) => a.message.localeCompare(b.message));
    errors.push(...missing);

    const missingParents: ValidationIssue[] = [];
    for (const group of listEligibleIgnoreGroups(schema)) {
        const parentEntry = schemaByKey[group.parent];
        if (!parentEntry || parentEntry.tab === 'BS' || parentEntry.bs_only) {
            continue;
        }
        for (const view of validViewsForLine(parentEntry, inputs.consolidated_only)) {
            if (!isGroupIgnored(inputs, view, group)) {
                continue;
            }
            if (allowedDrivers(schema, group.parent, view, inputs.consolidated_only, inputs).length === 0) {
                continue;
            }
            if (
                !hasNonzeroLast12History(
                    history,
                    view,
                    group.parent,
                    parentEntry,
                    inputs.consolidated_only,
                )
            ) {
                continue;
            }
            const active = inputs.drivers[view]?.[group.parent]?.active;
            if (!active) {
                missingParents.push(
                    issue(schemaByKey, sectionMap, {
                        code: VALIDATION_CODES.missing_driver,
                        line_key: group.parent,
                        view,
                        t: null,
                        message: `${parentEntry.label} (${view}) has history but no active driver.`,
                    }),
                );
            }
        }
    }
    missingParents.sort((a, b) => a.message.localeCompare(b.message));
    errors.push(...missingParents);

    for (const view of LOCATION_VIEWS) {
        if (!isStoreOpen(model, history, view, 0)) {
            continue;
        }
        for (const lineKey of [AVG_ORDERS_KEY, TICKET_KEY]) {
            const entry = schemaByKey[lineKey];
            const active = inputs.drivers[view]?.[lineKey]?.active;
            const historyLineKey = lineKey === AVG_ORDERS_KEY ? TOTAL_ORDERS_KEY : lineKey;
            const historyEntry = schemaByKey[historyLineKey];
            const hasNonzero = hasNonzeroLast12History(
                history,
                view,
                historyLineKey,
                historyEntry!,
                inputs.consolidated_only,
            );
            if (!active && hasNonzero) {
                errors.push(
                    issue(schemaByKey, sectionMap, {
                        code: VALIDATION_CODES.open_store_undriven,
                        line_key: lineKey,
                        view,
                        t: null,
                        message: `Open store has undriven ${entry?.label ?? lineKey}`,
                    }),
                );
            }
        }
    }

    for (const view of LOCATION_VIEWS) {
        if (!isLaborBuildupActive(inputs, view)) {
            continue;
        }
        const required =
            view === 'ga'
                ? ['is.salaried_headcount', 'is.avg_salary_annual']
                : [...LABOR_COMPONENT_KEYS];
        const missingComponents = required.filter((key) => !inputs.drivers[view]?.[key]?.active);
        if (missingComponents.length > 0) {
            const names = missingComponents
                .map((key) => schemaByKey[key]?.label ?? key)
                .join(', ');
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.labor_buildup_inputs,
                    line_key: PAYROLL_WAGES_KEY,
                    view,
                    t: null,
                    message: `Labor build-up requires driven inputs: ${names}`,
                }),
            );
        }
    }

    for (let t = 1; t <= 36; t += 1) {
        const assets = getNumeric(model, 'consolidated', TOTAL_ASSETS_KEY, t);
        const le = getNumeric(model, 'consolidated', TOTAL_LE_KEY, t);
        if (assets != null && le != null && Math.abs(assets - le) > 0.01) {
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.bs_identity,
                    line_key: TOTAL_ASSETS_KEY,
                    view: 'consolidated',
                    t,
                    message: `Total Assets and Total L&E differ by ${assets - le}`,
                }),
            );
        }
    }

    const consolidatedLines = model.values.consolidated ?? {};
    const hasCfKeys = Object.keys(consolidatedLines).some((key) => key.startsWith('cf.'));
    if (hasCfKeys) {
        const cfSchema = loadCashFlowSchema();
        const cfByKey = Object.fromEntries(cfSchema.map((entry) => [entry.key, entry]));

        const cfIssue = (
            partial: Omit<ValidationIssue, 'label' | 'section'>,
        ): ValidationIssue => ({
            ...partial,
            label: cfByKey[partial.line_key]?.label ?? partial.line_key,
            section: 'Cash Flow',
        });

        for (const diag of model.diagnostics) {
            if (diag.code !== VALIDATION_CODES.cf_not_tied || diag.lineKey == null || diag.t == null) {
                continue;
            }
            errors.push(
                cfIssue({
                    code: VALIDATION_CODES.cf_not_tied,
                    line_key: diag.lineKey,
                    view: diag.view ?? 'consolidated',
                    t: diag.t,
                    message: diag.message,
                }),
            );
        }

        const accrualByT = new Map<number, number>();
        const policyInputs = inputs.cash_policy;
        const splitSchedule = policyInputs?.distribution.split_schedule;
        const prefAccrual = splitSchedule?.pref_accrual;
        if (
            policyInputs?.enabled === true &&
            splitSchedule &&
            Array.isArray(prefAccrual)
        ) {
            for (const monthEntry of prefAccrual) {
                if (
                    !scheduleIsValid(splitSchedule, policyInputs, monthEntry.t, scheduleContext) ||
                    !isBalancedPrefAccrualReclass(monthEntry)
                ) {
                    continue;
                }
                let sum = 0;
                for (const [key, value] of Object.entries(monthEntry.lines)) {
                    if (key.startsWith('bs.preferred_equity_')) {
                        sum += value;
                    }
                }
                accrualByT.set(monthEntry.t, sum);
            }
        }

        const prefHist = history.values.consolidated?.['bs.plus_preferred_equity'];
        for (let t = 1; t <= 36; t += 1) {
            const issuance = getNumeric(model, 'consolidated', 'cf.issuance_of_preferred_equity', t);
            const prefCurr = getNumeric(model, 'consolidated', 'bs.plus_preferred_equity', t);
            let prefPrev: number | null;
            if (t === 1) {
                const raw = prefHist?.[history.nHistory - 1];
                prefPrev = typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
            } else {
                prefPrev = getNumeric(model, 'consolidated', 'bs.plus_preferred_equity', t - 1);
            }
            if (issuance == null || prefCurr == null || prefPrev == null) {
                continue;
            }
            const amount = issuance - ((prefCurr - prefPrev) - (accrualByT.get(t) ?? 0));
            if (Math.abs(amount) > 0.01) {
                warnings.push(
                    cfIssue({
                        code: VALIDATION_CODES.cf_other_equity_movement,
                        line_key: 'cf.issuance_of_preferred_equity',
                        view: 'consolidated',
                        t,
                        message: `Equity lines other than Preferred Equity moved ${amount} of cash in M+${t}; it is shown in Issuance of Preferred Equity.`,
                    }),
                );
            }
        }
    }

    for (const item of inputs.one_time_items) {
        const t = tFromMonthKey(lastClosed, item.month);
        const entry = schema.find((row) => row.key === item.line_key);
        if (t == null) {
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.one_time_invalid,
                    line_key: item.line_key,
                    view: item.view,
                    t: null,
                    message: `One-time month ${item.month} outside projection range`,
                }),
            );
            continue;
        }
        if (!entry) {
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.one_time_invalid,
                    line_key: item.line_key,
                    view: item.view,
                    t,
                    message: `Unknown line ${item.line_key}`,
                }),
            );
            continue;
        }
        if (entry.kind === 'input' && policyOwnedKeys.has(item.line_key)) {
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.one_time_invalid,
                    line_key: item.line_key,
                    view: item.view,
                    t,
                    message: `${entry.label} is set by the cash policy; remove this one-time item or disable the policy`,
                }),
            );
        }
        if ((entry.tab === 'BS' || entry.bs_only) && item.view !== 'consolidated') {
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.one_time_invalid,
                    line_key: item.line_key,
                    view: item.view,
                    t,
                    message: `${entry.label} one-time items must use consolidated view`,
                }),
            );
        }
        if (
            entry.tab === 'IS' &&
            !inputs.consolidated_only.includes(item.line_key) &&
            item.view === 'consolidated'
        ) {
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.one_time_invalid,
                    line_key: item.line_key,
                    view: item.view,
                    t,
                    message: `${entry.label} must use a location view, not consolidated`,
                }),
            );
        }
        if (inputs.consolidated_only.includes(item.line_key) && item.view !== 'consolidated') {
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.one_time_invalid,
                    line_key: item.line_key,
                    view: item.view,
                    t,
                    message: `${entry.label} must use consolidated view`,
                }),
            );
        }
        if (item.amount == null || !Number.isFinite(item.amount)) {
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.one_time_invalid,
                    line_key: item.line_key,
                    view: item.view,
                    t,
                    message: 'One-time amount must be finite',
                }),
            );
        }
        if (entry.kind !== 'input') {
            errors.push(
                issue(schemaByKey, sectionMap, {
                    code: VALIDATION_CODES.one_time_invalid,
                    line_key: item.line_key,
                    view: item.view,
                    t,
                    message: `${entry.label} is not an input line`,
                }),
            );
        }
    }

    if (options.caseName && options.otherNames?.some((name) => name.trim().toLowerCase() === options.caseName!.trim().toLowerCase())) {
        errors.push(
            issue(schemaByKey, sectionMap, {
                code: VALIDATION_CODES.duplicate_name,
                line_key: '',
                view: 'consolidated',
                t: null,
                message: `Duplicate case name ${options.caseName}`,
            }),
        );
    }
    if (options.expiresOn && options.today && options.today > options.expiresOn) {
        errors.push(
            issue(schemaByKey, sectionMap, {
                code: VALIDATION_CODES.expired,
                line_key: '',
                view: 'consolidated',
                t: null,
                message: `Case expired on ${options.expiresOn}`,
            }),
        );
    }

    if (!isEmptyCase) {
        for (let t = 1; t <= 36; t += 1) {
            for (const entry of schema) {
                if (entry.kind !== 'input') {
                    continue;
                }
                if (isFixedDriverLine(entry) && entry.key !== CASH_KEY) {
                    continue;
                }
                for (const view of validViewsForLine(entry, inputs.consolidated_only)) {
                    const active = inputs.drivers[view]?.[entry.key]?.active;
                    if (active === 'carry_forward' && entry.key !== CASH_KEY) {
                        continue;
                    }
                    const projected = getNumeric(model, view, entry.key, t);
                    if (projected == null) {
                        continue;
                    }
                    const monthKey = monthKeyFromT(lastClosed, t);
                    const hasIncrementalOneTime = inputs.one_time_items.some(
                        (item) =>
                            item.view === view &&
                            item.line_key === entry.key &&
                            item.month === monthKey &&
                            item.mode === 'incremental',
                    );
                    if (hasIncrementalOneTime) {
                        continue;
                    }
                    const histSeries = history.values[view]?.[entry.key] ?? [];
                    const histMax = Math.max(
                        0,
                        ...histSeries
                            .slice(-12)
                            .map((value) => (typeof value === 'number' ? Math.abs(value) : 0)),
                    );
                    const histAllMax = Math.max(
                        0,
                        ...histSeries.map((value) => (typeof value === 'number' ? Math.abs(value) : 0)),
                    );
                    const exceedsMultiple =
                        histMax > 0 && Math.abs(projected) > thresholds.outlier_multiple * histMax;
                    const exceedsAbs =
                        histAllMax < thresholds.outlier_abs &&
                        Math.abs(projected) > thresholds.outlier_abs;
                    if (exceedsMultiple || exceedsAbs) {
                        warnings.push(
                            issue(schemaByKey, sectionMap, {
                                code: VALIDATION_CODES.outlier,
                                line_key: entry.key,
                                view,
                                t,
                                message: `${entry.label} projected value is an outlier at t=${t}`,
                            }),
                        );
                    }
                    const negativesInHist = histSeries
                        .slice(-12)
                        .every((value) => typeof value !== 'number' || value >= 0);
                    if (negativesInHist && projected < 0) {
                        warnings.push(
                            issue(schemaByKey, sectionMap, {
                                code: VALIDATION_CODES.new_negative,
                                line_key: entry.key,
                                view,
                                t,
                                message: `${entry.label} has new negative value at t=${t}`,
                            }),
                        );
                    }
                }
            }

            const cash = getNumeric(model, 'consolidated', CASH_KEY, t);
            if (cash != null && cash < 0) {
                warnings.push(
                    issue(schemaByKey, sectionMap, {
                        code: VALIDATION_CODES.cash_negative,
                        line_key: CASH_KEY,
                        view: 'consolidated',
                        t,
                        message: `Cash is negative at t=${t}`,
                    }),
                );
            }

            for (const view of LOCATION_VIEWS) {
                const daysClosed = getNumeric(model, view, DAYS_CLOSED_KEY, t);
                const calendarDays = daysInMonth(lastClosed, t);
                if (daysClosed != null && daysClosed > calendarDays) {
                    warnings.push(
                        issue(schemaByKey, sectionMap, {
                            code: VALIDATION_CODES.days_open_overflow,
                            line_key: DAYS_CLOSED_KEY,
                            view,
                            t,
                            message: `${schemaByKey[DAYS_CLOSED_KEY]?.label ?? DAYS_CLOSED_KEY} exceeds calendar days at t=${t}`,
                        }),
                    );
                }
            }
        }

        for (const [viewName, lines] of Object.entries(inputs.drivers)) {
            const view = viewName as View;
            for (const [lineKey, state] of Object.entries(lines)) {
                if (!state.active) {
                    continue;
                }
                const entry = schemaByKey[lineKey];
                if (
                    state.active === 'dollar_per_sq_ft' ||
                    state.active === 'dollar_per_1000_sqft_calendar_day'
                ) {
                    for (let t = 1; t <= 36; t += 1) {
                        const sqft = getNumeric(model, view, SQFT_KEY, t) ?? 0;
                        if (sqft === 0 && state.by_type[state.active]?.values[t - 1] != null) {
                            warnings.push(
                                issue(schemaByKey, sectionMap, {
                                    code: VALIDATION_CODES.zero_denominator,
                                    line_key: lineKey,
                                    view,
                                    t,
                                    message: `${entry?.label ?? lineKey} square footage is zero for ${state.active} at t=${t}`,
                                }),
                            );
                        }
                    }
                }
                if (state.active === 'dollar_per_fte') {
                    for (let t = 1; t <= 36; t += 1) {
                        const boh = getNumeric(model, view, BOH_KEY, t) ?? 0;
                        const foh = getNumeric(model, view, FOH_KEY, t) ?? 0;
                        const hourly = boh + foh;
                        const salaried =
                            view === 'ga' ? (getNumeric(model, view, 'is.salaried_headcount', t) ?? 0) : 0;
                        const denom = hourly > 0 ? hourly : salaried;
                        if (denom === 0 && state.by_type[state.active]?.values[t - 1] != null) {
                            warnings.push(
                                issue(schemaByKey, sectionMap, {
                                    code: VALIDATION_CODES.zero_denominator,
                                    line_key: lineKey,
                                    view,
                                    t,
                                    message: `${entry?.label ?? lineKey} FTE total is zero for dollar_per_fte at t=${t}`,
                                }),
                            );
                        }
                    }
                }
                if (state.active === 'count_per_open_day' || state.active === 'dollar_per_open_day') {
                    for (let t = 1; t <= 36; t += 1) {
                        const daysOpen = getNumeric(model, view, DAYS_OPEN_KEY, t) ?? 0;
                        if (daysOpen === 0 && state.by_type[state.active]?.values[t - 1] != null) {
                            warnings.push(
                                issue(schemaByKey, sectionMap, {
                                    code: VALIDATION_CODES.zero_denominator,
                                    line_key: lineKey,
                                    view,
                                    t,
                                    message: `${entry?.label ?? lineKey} days open is zero for ${state.active} at t=${t}`,
                                }),
                            );
                        }
                    }
                }
                const values = state.by_type[state.active]?.values ?? [];
                if (state.active === 'mom_pct' || state.active === 'yoy_pct') {
                    for (let i = 0; i < values.length; i += 1) {
                        const value = values[i];
                        if (value == null) {
                            continue;
                        }
                        const max =
                            state.active === 'mom_pct'
                                ? thresholds.mom_pct_max
                                : thresholds.yoy_pct_max;
                        if (Math.abs(value) > max) {
                            warnings.push(
                                issue(schemaByKey, sectionMap, {
                                    code: VALIDATION_CODES.mom_yoy_extreme,
                                    line_key: lineKey,
                                    view,
                                    t: i + 1,
                                    message: `${entry?.label ?? lineKey} ${state.active} exceeds threshold at t=${i + 1}`,
                                }),
                            );
                        }
                    }
                }
                if (['dio', 'dso', 'dpo'].includes(state.active)) {
                    for (let i = 0; i < values.length; i += 1) {
                        const value = values[i];
                        if (value != null && value > thresholds.days_max) {
                            warnings.push(
                                issue(schemaByKey, sectionMap, {
                                    code: VALIDATION_CODES.days_over_365,
                                    line_key: lineKey,
                                    view,
                                    t: i + 1,
                                    message: `${entry?.label ?? lineKey} ${state.active} exceeds ${thresholds.days_max} days at t=${i + 1}`,
                                }),
                            );
                        }
                    }
                }
                if (state.active === 'dollar_per_pay_period') {
                    const payParams = parsePayPeriodParams(state.by_type.dollar_per_pay_period?.params);
                    if (payParams) {
                        const m1 = monthKeyFromT(lastClosed, 1);
                        const year = Number(m1.slice(0, 4));
                        const month = Number(m1.slice(5, 7));
                        if (Number.isFinite(year) && Number.isFinite(month)) {
                            const cutoffUtc = Date.UTC(year, month - 1 - 24, 1);
                            const anchorUtc = Date.UTC(
                                Number(payParams.anchor_date.slice(0, 4)),
                                Number(payParams.anchor_date.slice(5, 7)) - 1,
                                Number(payParams.anchor_date.slice(8, 10)),
                            );
                            if (anchorUtc < cutoffUtc) {
                                warnings.push(
                                    issue(schemaByKey, sectionMap, {
                                        code: VALIDATION_CODES.pay_period_anchor_stale,
                                        line_key: lineKey,
                                        view,
                                        t: null,
                                        message: `${entry?.label ?? lineKey} pay-period anchor is more than 24 months before M+1`,
                                    }),
                                );
                            }
                        }
                    }
                }
            }
        }
    }

    if (inputs.consolidated_only.length > 0) {
        const isFormulaParents = schema.filter(
            (entry) =>
                (entry.kind === 'subtotal' || entry.kind === 'metric') &&
                entry.formula &&
                entry.key !== GPS_KEY &&
                entry.key !== TICKET_KEY,
        );
        const warnedConsolidatedOnly = new Set<string>();

        for (const lineKey of inputs.consolidated_only) {
            const entry = schemaByKey[lineKey];
            if (!entry || entry.kind !== 'input' || entry.tab !== 'IS') {
                continue;
            }
            const parents = isFormulaParents.filter((parent) =>
                formulaDependencies(parent.formula).includes(lineKey),
            );
            for (const parent of parents) {
                const deps = formulaDependencies(parent.formula);
                for (let t = 1; t <= 36; t += 1) {
                    const lookup: Record<string, number | null> = {};
                    for (const dep of deps) {
                        lookup[dep] = getNumeric(model, 'consolidated', dep, t) ?? 0;
                    }
                    const computed = evalFormula(parent.formula, lookup);
                    const stored = getNumeric(model, 'consolidated', parent.key, t);
                    if (
                        computed != null &&
                        stored != null &&
                        Math.abs(computed - stored) > 0.01
                    ) {
                        const warnKey = `${lineKey}:${t}`;
                        if (warnedConsolidatedOnly.has(warnKey)) {
                            continue;
                        }
                        warnedConsolidatedOnly.add(warnKey);
                        warnings.push(
                            issue(schemaByKey, sectionMap, {
                                code: VALIDATION_CODES.consolidated_only_not_in_total,
                                line_key: lineKey,
                                view: 'consolidated',
                                t,
                                message: `${entry.label} is not reflected in ${parent.label} at t=${t}`,
                            }),
                        );
                    }
                }
            }
        }
    }

    const policy = inputs.cash_policy;
    if (policy?.enabled) {
        const policyIssue = (
            code: string,
            message: string,
            t: number | null = null,
            lineKey = 'cash_policy',
        ): ValidationIssue => ({
            code,
            line_key: lineKey,
            view: 'consolidated',
            t,
            message,
            label: 'Cash policy',
            section: 'Cash policy',
        });

        const moneyFields = [
            policy.floor.operating_minimum,
            policy.floor.drawdown_allowance,
            policy.distribution.amount_per_period,
            ...(policy.sweep.opening_mm_balance != null ? [policy.sweep.opening_mm_balance] : []),
            ...(policy.sweep.amount_per_period != null ? [policy.sweep.amount_per_period] : []),
        ];
        const moneyInvalid = moneyFields.some(
            (value) => value == null || !Number.isFinite(value) || value < 0,
        );
        const aprInvalid =
            policy.sweep.yield_snapshot?.apr_by_month.some(
                (apr) => apr == null || !Number.isFinite(apr) || apr < 0 || apr > 0.25,
            ) ?? false;
        if (moneyInvalid || aprInvalid) {
            errors.push(policyIssue(VALIDATION_CODES.policy_negative, 'Cash policy has negative or out-of-range values'));
        }

        const anchor = policy.distribution.anchor_date;
        const anchorValid = /^\d{4}-\d{2}-\d{2}$/.test(anchor);
        if (
            !anchorValid ||
            (policy.distribution.cadence === 'biweekly' && !anchor)
        ) {
            errors.push(policyIssue(VALIDATION_CODES.policy_anchor_invalid, 'Anchor date is invalid for the selected cadence'));
        }

        const oneTimeRaw = policy.distribution.one_time;
        const hasOneTimeDate = Boolean(oneTimeRaw?.date && oneTimeRaw.date.trim() !== '');
        const oneTimeAmount = oneTimeRaw?.amount;
        const hasOneTimeAmount =
            oneTimeAmount != null && Number.isFinite(oneTimeAmount) && oneTimeAmount !== 0;
        if (
            (hasOneTimeDate && !hasOneTimeAmount && oneTimeAmount !== 0) ||
            (hasOneTimeAmount && !hasOneTimeDate)
        ) {
            errors.push(
                policyIssue(
                    VALIDATION_CODES.policy_one_time_invalid,
                    'One-time extra requires both date and amount',
                ),
            );
        } else if (hasOneTimeDate && hasOneTimeAmount) {
            const extraDate = oneTimeRaw!.date;
            if (!/^\d{4}-\d{2}-\d{2}$/.test(extraDate)) {
                errors.push(
                    policyIssue(
                        VALIDATION_CODES.policy_one_time_invalid,
                        'One-time extra date must be YYYY-MM-DD',
                    ),
                );
            } else if (oneTimeAmount! < 0) {
                errors.push(
                    policyIssue(
                        VALIDATION_CODES.policy_one_time_invalid,
                        'One-time extra amount must be non-negative',
                    ),
                );
            } else {
                const closedMonthKey = lastClosed.slice(0, 7);
                const extraMonthKey = extraDate.slice(0, 7);
                if (extraMonthKey <= closedMonthKey) {
                    const lastDistDate = options.lastDistDate ?? null;
                    const lastDistNum = options.lastDistNum ?? null;
                    if (lastDistDate && lastDistNum != null && extraDate <= lastDistDate) {
                        warnings.push(
                            policyIssue(
                                VALIDATION_CODES.policy_one_time_spent,
                                `One-time extra dated ${extraDate} is already in actuals (distribution #${lastDistNum} on ${lastDistDate}) and is ignored. Clear it.`,
                            ),
                        );
                    } else {
                        warnings.push(
                            policyIssue(
                                VALIDATION_CODES.policy_one_time_spent,
                                `One-time extra dated ${extraDate} falls in a closed month and is ignored. Clear it.`,
                            ),
                        );
                    }
                } else {
                    let inHorizon = false;
                    for (let t = 1; t <= 36; t += 1) {
                        if (monthKeyFromT(lastClosed, t) === extraDate.slice(0, 7)) {
                            inHorizon = true;
                            break;
                        }
                    }
                    if (!inHorizon) {
                        errors.push(
                            policyIssue(
                                VALIDATION_CODES.policy_one_time_invalid,
                                'One-time extra date must fall within the 36-month projection window',
                            ),
                        );
                    }
                }
            }
        }

        const view = policy.sweep.yield_income_view;
        if (
            view != null &&
            view !== 'river_oaks' &&
            view !== 'highland_park' &&
            view !== 'ga'
        ) {
            errors.push(policyIssue(VALIDATION_CODES.policy_view_invalid, 'Interest income view is invalid'));
        }

        if (!policy.distribution.split_schedule) {
            errors.push(policyIssue(VALIDATION_CODES.policy_schedule_missing, 'Waterfall split schedule is missing'));
        } else {
            const schedule = policy.distribution.split_schedule;
            const basis = schedule.basis;
            const lastDistNum = options.lastDistNum ?? null;
            const staleFields: string[] = [];
            if (basis.anchor_date !== policy.distribution.anchor_date) {
                staleFields.push('Anchor date');
            }
            if (basis.cadence !== policy.distribution.cadence) {
                staleFields.push('Cadence');
            }
            if (
                policy.distribution.amount_per_period == null ||
                Math.round(basis.amount_per_period * 100) !==
                    Math.round(policy.distribution.amount_per_period * 100)
            ) {
                staleFields.push('Amount per period');
            }
            if (basis.last_dist_num !== (lastDistNum ?? null)) {
                staleFields.push('Last distribution number');
            }
            if (options.lastClosedMonth != null) {
                if (!basis.last_closed_month || basis.last_closed_month !== options.lastClosedMonth) {
                    staleFields.push('Last closed month');
                }
            }
            if (options.waterfallMode != null && basis.mode !== options.waterfallMode) {
                staleFields.push('Mode');
            }
            if (basis.horizon_months !== 36) {
                staleFields.push('Horizon');
            }
            const policyExtra = effectiveOneTime(
                policy,
                options.lastDistDate ?? null,
                lastClosed,
            );
            const basisExtraDate = basis.one_time_date ?? null;
            const basisExtraAmount = basis.one_time_amount ?? null;
            if (policyExtra) {
                if (basisExtraDate !== policyExtra.date) {
                    staleFields.push('One-time extra');
                } else if (
                    basisExtraAmount == null ||
                    Math.round(basisExtraAmount * 100) !== Math.round(policyExtra.amount * 100)
                ) {
                    staleFields.push('One-time extra');
                }
            } else if (
                basisExtraDate != null ||
                (basisExtraAmount != null && basisExtraAmount !== 0)
            ) {
                staleFields.push('One-time extra');
            }
            if (staleFields.length > 0) {
                errors.push(
                    policyIssue(
                        VALIDATION_CODES.policy_schedule_stale,
                        `Waterfall split schedule is stale: ${staleFields.join(', ')}`,
                    ),
                );
            }

            const scheduleBasisCurrent = scheduleIsValid(schedule, policy, 36, scheduleContext);

            const ownedKeys = policyOwnedLineKeys(schema);
            const distLineKeys = distributionPartnerKeys(schema);
            let scheduleInvalid = false;
            const scheduleMonthsByT = new Map(schedule.months.map((m) => [m.t, m]));
            if (scheduleBasisCurrent) {
            for (let t = 1; t <= 36; t += 1) {
                const monthKey = monthKeyFromT(lastClosed, t);
                const extraInMonth = oneTimeInMonth(
                    policy,
                    monthKey,
                    options.lastDistDate ?? null,
                    lastClosed,
                );
                const skipDate = extraInMonth?.date ?? null;
                const nPolicy = policyDatesInMonth(
                    policy.distribution.cadence,
                    policy.distribution.anchor_date,
                    monthKey,
                    options.lastDistDate ?? null,
                    skipDate,
                );
                const extraDist = extraInMonth?.amount ?? 0;
                if (nPolicy === 0 && extraDist === 0) {
                    continue;
                }
                const month = scheduleMonthsByT.get(t);
                if (!month) {
                    scheduleInvalid = true;
                    break;
                }
                if (month.t < 1 || month.t > 36) {
                    scheduleInvalid = true;
                    break;
                }
                const expected =
                    (policy.distribution.amount_per_period ?? 0) * nPolicy + extraDist;
                let sum = 0;
                for (const [lineKey, amount] of Object.entries(month.lines)) {
                    if (!distLineKeys.has(lineKey)) {
                        scheduleInvalid = true;
                        break;
                    }
                    sum += amount;
                }
                if (Math.abs(sum - expected) >= 0.005) {
                    scheduleInvalid = true;
                    break;
                }
            }
            const prefReclassLineRe = /^bs\.(preferred_equity|allocated_income)_partner_\d+$/;
            for (const monthEntry of schedule.pref_accrual ?? []) {
                if (scheduleInvalid) {
                    break;
                }
                if (monthEntry.t < 1 || monthEntry.t > 36) {
                    scheduleInvalid = true;
                    break;
                }
                let sum = 0;
                for (const [lineKey, amount] of Object.entries(monthEntry.lines)) {
                    if (!prefReclassLineRe.test(lineKey) || !schemaByKey[lineKey]) {
                        scheduleInvalid = true;
                        break;
                    }
                    sum += amount;
                }
                if (scheduleInvalid) {
                    break;
                }
                if (Math.abs(sum) >= 0.005) {
                    scheduleInvalid = true;
                    break;
                }
            }
            }
            void ownedKeys;
            const placeholderMonths = (model.cash_policy?.months ?? []).filter(
                (month) => month.split_source === 'tier4_placeholder',
            );
            if (scheduleBasisCurrent && !scheduleInvalid && placeholderMonths.length > 0) {
                errors.push(
                    policyIssue(
                        VALIDATION_CODES.policy_schedule_invalid,
                        `Distribution split fell back to the Tier 4 placeholder in ${placeholderMonths.length} month(s) starting ${placeholderMonths[0]!.month}; refresh the waterfall split or fix the partner mapping`,
                        placeholderMonths[0]!.t,
                    ),
                );
            }
            if (scheduleInvalid) {
                errors.push(
                    policyIssue(
                        VALIDATION_CODES.policy_schedule_invalid,
                        'Waterfall split schedule has invalid months or line totals',
                    ),
                );
            }
        }

        if (policy.sweep.enabled && !policy.sweep.yield_snapshot) {
            warnings.push(
                policyIssue(
                    VALIDATION_CODES.policy_yield_unavailable,
                    'Money market yield snapshot is unavailable; interest modelled at 0%',
                ),
            );
        }

        const today = options.today ?? new Date().toISOString().slice(0, 10);
        const asOf = policy.sweep.yield_snapshot?.as_of;
        if (asOf) {
            const asOfDate = new Date(asOf.slice(0, 10));
            const todayDate = new Date(today.slice(0, 10));
            const diffDays = (todayDate.getTime() - asOfDate.getTime()) / (1000 * 60 * 60 * 24);
            if (diffDays > 30) {
                warnings.push(
                    policyIssue(
                        VALIDATION_CODES.policy_yield_old,
                        `Yield snapshot as of ${asOf} is more than 30 days old`,
                    ),
                );
            }
        }

        if (
            options.solverMax !== undefined &&
            policy.distribution.amount_per_period != null &&
            policy.distribution.amount_per_period > options.solverMax
        ) {
            warnings.push(
                policyIssue(
                    VALIDATION_CODES.policy_above_solver,
                    `Amount per period exceeds solver maximum of ${options.solverMax}`,
                ),
            );
        }

        if (effectiveFloor(policy) === 0) {
            warnings.push(
                policyIssue(
                    VALIDATION_CODES.policy_floor_unsized,
                    'Floor is zero',
                ),
            );
        }

        const floorForBreach = effectiveFloor(policy);
        for (let t = 1; t <= 36; t += 1) {
            const gridCash = getNumeric(model, 'consolidated', CASH_KEY, t);
            if (gridCash == null || gridCash >= floorForBreach - 0.005) {
                continue;
            }
            const shortfall = Math.round((floorForBreach - gridCash) * 100) / 100;
            warnings.push(
                policyIssue(
                    VALIDATION_CODES.policy_floor_breach,
                    `Operating cash below floor at month ${monthKeyFromT(lastClosed, t)} (shortfall ${shortfall})`,
                    t,
                    CASH_KEY,
                ),
            );
        }
    }

    void monthKeyFromT;

    return { errors, warnings };
}
